import {
  DestroyRef,
  inject,
  Injectable,
  Renderer2,
  RendererFactory2,
  TemplateRef,
} from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/overlay';
import { WindowRef } from '../window/window-ref';
import { Observable, tap } from 'rxjs';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class ModalService {
  #dialog = inject(MatDialog);
  #rendererFactory = inject(RendererFactory2);
  #winRef = inject(WindowRef);
  #route = inject(Router);
  #destroyRef = inject(DestroyRef);

  private renderer: Renderer2;
  public afterClosedModal$ = this.#dialog.afterAllClosed;

  constructor() {
    this.renderer = this.#rendererFactory.createRenderer(null, null);

    this.#dialog.afterOpened
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() =>
        this.renderer.addClass(this.#winRef.document.body, 'modal-open'),
      );

    this.afterClosedModal$
      .pipe(
        tap(() =>
          this.renderer.removeClass(this.#winRef.document.body, 'modal-open'),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  open<T, D, R>(
    component: ComponentType<T> | TemplateRef<T>,
    config?: MatDialogConfig<D>,
    closeOnNavigation = true,
  ): MatDialogRef<T, R> {
    if (closeOnNavigation) {
      this.#route.events
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe((event) => {
          if (event instanceof NavigationStart) {
            if (this.isOpen()) {
              this.close();
            }
          }
        });
    }
    return this.#dialog.open<T, D, R>(component, config);
  }

  close(): void {
    this.#dialog.closeAll();
  }

  isOpen(): boolean {
    return this.#dialog.openDialogs.length > 0;
  }

  closeWithCallback(): Observable<void> {
    this.#dialog.closeAll();
    return this.afterClosedModal$;
  }
}
